import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Autocomplete from "@mui/material/Autocomplete";
import ListItemButton from "@mui/material/ListItemButton";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";

import { ButtonUI } from "../Common";
import { FormTile } from "../Common/Tile";
import TextField from "@mui/material/TextField";
import Salesforce from "../../service/salesforce";
import CommonLayout from "../Common/Layout";
import { navDataConnection } from "../kmk/AppData";

const useStyles = makeStyles()((theme) => ({
  HeaderTitle: {
    fontSize: 20,
    width: "200px",
    display: "inline-block",
  },
  container: {
    padding: 0,
    backgroundColor: "#FFFFFF",
  },
  subContainer: {
    display: "flex",
    padding: 0,
    borderTop: 0,
    width: "100%",
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    padding: "0px",
    textAlign: "center",
    minHeight: "250px",
  },
  inputLabel: {
    display: "inline-flex",
    fontSize: "16px",
    color: "rgba(0, 0, 0, 0.85)",
    fontVariant: "tabular-nums",
    fontFeatureSettings: `"tnum","tnum"`,
  },
  PanelContainer: {
    padding: "22px 30px 30px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "250px",
    marginLeft: "inherit",
  },
  BigTileContainer: {
    paddingTop: "0px !important",
    marginTop: "10px !important",
  },
  DataTableContainer: {
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: "5px 0px 0px",
    overflowX: "auto",
    "& .MuiTableRow-head .MuiTableCell-root": {
      color: "#A2A2A2",
      padding: "6px 16px",
      height: "37px",
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "1px solid #e0e0e0",
    },
    "& .MuiTablePagination-toolbar": {
      minHeight: "40px",
    },
    "& P.MuiTablePagination-selectLabel,P.MuiTablePagination-displayedRows": {
      marginBottom: "0px !important",
    },
    "& ::-webkit-scrollbar-track": {
      marginTop: "36px",
    },
  },
}));

export default function SalesforceExplorer() {
  const [workspaceName, setWorkspaceName] = useState("");
  const [URL, setURL] = useState("");
  const [connectionName, setConnectionName] = useState("");
  const [loading, setLoading] = useState(false);
  const [objectList, setObjectList] = useState([]);
  const [fieldList, setFieldList] = useState([]);
  const [soqlQuery, setSoqlQuery] = useState("");
  const [queryResult, setQueryResult] = useState([]);
  const [queryError, setQueryError] = useState("");
  const [tableNoDatatext, setTableNoDatatext] = useState("");
  const [fieldNoDatatext, setFieldNoDatatext] = useState("Object's fields will appear here.");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [columns, setColumns] = useState([{ name: "", width: 120, align: "left" }]);

  const { classes } = useStyles();
  const { Workspace_id, Stage_id } = useParams();

  const navigate = useNavigate();

  const runQuery = async () => {
    setLoading(true);
    setQueryResult([]);
    setQueryError("");
    let response = null;
    setColumns([{ name: "", minWidth: 120, align: "left" }]);

    let submitData = {
      query: soqlQuery,
    };

    const salesforce = new Salesforce();
    response = await salesforce.runQuery(Workspace_id, Stage_id, submitData);

    if (response.success) {
      if (!response.data.error) {
        if (response.data.count) {
          const columns = [];
          let tableData = [];

          response.data.data.forEach((data, key) => {
            let dataObject = [];
            Object.keys(data).forEach((col) => {
              if (key === 0) columns.push({ name: col, minWidth: 120, align: "left" });
              dataObject.push({ value: data[col], align: "left" });
            });
            tableData.push(dataObject);
          });

          setColumns(columns);
          setQueryResult(tableData);
        } else {
          setTableNoDatatext("No records found");
        }
      } else setQueryError(response.data.error && response.data.result);
    }

    setLoading(false);
  };

  const getFields = async (value) => {
    setLoading(true);

    const salesforce = new Salesforce();
    const result = await salesforce.getFields(Workspace_id, Stage_id, value);

    if (result.data && Object.keys(result.data).length) {
      setFieldList(result.data.fields);
    } else {
      setFieldNoDatatext("No fields found");
    }
    setLoading(false);
  };

  useEffect(() => {
    const getObjects = async () => {
      setLoading(true);

      const salesforce = new Salesforce();
      const result = await salesforce.getObjects(Workspace_id, Stage_id);

      if (result.success && Object.keys(result.data).length) {
        setConnectionName(result.data.Name);
        setURL(result.data.URL);
        setConnectionName(result.data.Name);
        setWorkspaceName(result.data.workspace?.Name || "");
        setObjectList(result.data.objects);
      } else if (!result.success || result.status === "fail") {
        navigate(`/my-workspaces/${Workspace_id}/${navDataConnection.link}`);
      }
      setLoading(false);
    };

    getObjects();

    return () => setColumns([{ name: "", minWidth: 120, align: "left" }]);
  }, [Workspace_id, Stage_id, navigate]);

  const breadcrumbArr = [
    { link: "/my-workspaces", displayName: "Workspaces", active: false },
    { link: `/my-workspaces/${Workspace_id}`, displayName: workspaceName, active: false },
    {
      link: `/my-workspaces/${Workspace_id}/${navDataConnection.link}`,
      displayName: "Data Connections",
      active: false,
    },
    { link: "#", displayName: connectionName, active: true },
  ];

  const formSection = () => {
    return (
      <Box>
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>
        <Box component="form" className={classes.formContainer}>
          <Grid container spacing={3}>
            <Grid textAlign="left" size={12}>
              <InputLabel className={classes.inputLabel}>URL: </InputLabel>
              <ListItemButton
                component="a"
                href={URL}
                target="_blank"
                children={URL}
                style={{ color: "#1976d2", display: "inline-flex" }}
                underline="hover"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <Grid container spacing={3} direction={"column"}>
                <Grid size={12}>
                  <Autocomplete
                    fullWidth
                    options={objectList}
                    getOptionLabel={(option) => {
                      return option.text || "";
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option.value}>
                        {option.text}
                      </li>
                    )}
                    renderInput={(params) => <TextField variant="outlined" {...params} label="Objects" />}
                    onChange={(e, selectedOptionObj) => {
                      if (selectedOptionObj) getFields(selectedOptionObj.value);
                      else {
                        setFieldList([]);
                        setFieldNoDatatext("Object's fields will appear here.");
                      }
                    }}
                    ListboxProps={{
                      style: {
                        maxHeight: "200px",
                      },
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel shrink>Fields</InputLabel>
                    <Select
                      multiple
                      native
                      label="Fields"
                      inputProps={{
                        style: {
                          height: "62px",
                          maxHeight: "62px",
                          cursor: "default",
                          overflowY: "auto",
                        },
                      }}
                      sx={{ padding: "10px 0" }}
                    >
                      {fieldList.length ? (
                        fieldList.map((option) => (
                          <option key={option.value} value={option.value} disabled={true}>
                            {option.text}
                          </option>
                        ))
                      ) : (
                        <option key={0} value={0} disabled={true}>
                          {fieldNoDatatext}
                        </option>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 8,
              }}
            >
              <Grid container spacing={3} direction={"column"}>
                <Grid size={12}>
                  <TextField
                    required
                    variant="outlined"
                    value={soqlQuery}
                    fullWidth
                    label="SOQL"
                    multiline
                    minRows={7}
                    maxRows={7}
                    onChange={(e) => setSoqlQuery(e.target.value)}
                  />
                </Grid>

                <Grid sx={{ justifyContent: "flex-end", display: "flex" }} size={12}>
                  <ButtonUI
                    variant="contained"
                    color="success"
                    disabled={loading || !soqlQuery.trim()}
                    onClick={runQuery}
                  >
                    Run Query
                  </ButtonUI>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  };

  return (
    <CommonLayout title="Data Connections" loading={loading} breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>

        <Box maxWidth="lg" sx={{ mb: 4, mx: 0 }}>
          <Box className={classes.subContainer}>
            <Box className={classes.PanelContainer}>
              <Grid container spacing={5} direction="row" sx={{ mb: 4 }}>
                <Grid size={12}>
                  <Typography className={classes.HeaderTitle} component="div">
                    Salesforce Explorer
                  </Typography>
                </Grid>
                <Grid className={classes.BigTileContainer} size={12}>
                  <FormTile>{formSection()}</FormTile>
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid size={12}>
                  <Typography className={classes.HeaderTitle} component="div">
                    Results
                  </Typography>
                </Grid>
                <Grid className={classes.BigTileContainer} size={12}>
                  <Paper sx={{ width: "100%", overflow: "hidden" }} className={classes.DataTableContainer}>
                    <TableContainer sx={{ maxHeight: 500 }}>
                      <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                          <TableRow>
                            {columns.map((column) => (
                              <TableCell key={column.name} align={column.align} style={{ minWidth: column.minWidth }}>
                                {column.name}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {queryResult.length ? (
                            queryResult.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, key) => {
                              return (
                                <TableRow key={key} hover role="checkbox" tabIndex={-1}>
                                  {columns.map((column, key) => {
                                    return (
                                      <TableCell key={column.name} align={column.align} style={{ height: 33 }}>
                                        {![undefined, null].includes(row[key]?.value) ? `${row[key]?.value}` : ""}
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              );
                            })
                          ) : (
                            <TableRow key={0} hover role="checkbox" tabIndex={-1}>
                              <TableCell key={0} align={queryError ? "left" : "center"}>
                                <Box
                                  sx={
                                    queryError
                                      ? {
                                          whiteSpace: "break-spaces",
                                          color: (theme) => theme.palette.error.main,
                                        }
                                      : {}
                                  }
                                >
                                  {queryError || tableNoDatatext || "Query results will appear here."}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      rowsPerPageOptions={[100, 500, 1000]}
                      component="div"
                      count={queryResult.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={(e, newPage) => setPage(newPage)}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                      }}
                    />
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>
    </CommonLayout>
  );
}
